import { domAnimation, LazyMotion } from 'framer-motion';
import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';

import ContentMedia from 'components/ContentMedia/ContentMedia';
import LinkComp from 'components/LinkComp/LinkComp';
import Text from 'components/Text/Text';
import { WorkProjectsTypes } from 'containers/WorkPage/WorkPage.types';
import { redirectToPageUrl } from 'utils';
import { tilesMotionProps } from 'utils/styles/animations';

import * as S from './GridItem.styles';

export interface GridItemProps extends WorkProjectsTypes {
  onHoverStart: (headerText, headerColoredText, color) => void;
  onHoverEnd: () => void;
  isMobile?: boolean;
  activeId?: string;
  setActiveId?: (prop: string) => void;
}

const defaultProps: Partial<GridItemProps> = {
  id: '1',
  heading: '',
  image: { url: '' },
  video: { url: '' },
  mediaType: 'image',
  aspectRatio: '_16x9',
  headerText: '',
  headerColoredText: '',
  gridViewTextColor: '',
  defaultBlackHeading: false,
  color: '',
  redirectTo: 'CaseStudyPage',
  caseStudy: { slug: 'test', writingDate: '01/01/1960' },
  isMobile: false,
};

const GridItem: FunctionComponent<GridItemProps> = ({
  id,
  heading,
  image,
  video,
  videoErrorMessage,
  mediaType,
  aspectRatio,
  headerText,
  headerColoredText,
  gridViewTextColor,
  defaultBlackHeading,
  color,
  redirectTo,
  newsArticle,
  eventPage,
  caseStudy,
  awardPage,
  insightArticle,
  onHoverStart,
  onHoverEnd,
  isMobile,
  activeId,
  setActiveId,
  ...rest
}) => {
  const [isDoubleClicked, setIsDoubleClicked] = useState<boolean>(false);

  const pages = {
    awardPage,
    eventPage,
    newsArticle,
    caseStudy,
    insightArticle,
  };

  useEffect(() => {
    isMobile && activeId !== id && setIsDoubleClicked(false);
  }, [isMobile, activeId, id]);

  const mobileRedirectionCond = isMobile
    ? isDoubleClicked
      ? redirectToPageUrl(redirectTo, pages)
      : null
    : redirectToPageUrl(redirectTo, pages);

  const handleOnMobileClick = useCallback(() => {
    setActiveId(id);
    setIsDoubleClicked(true);
    onHoverStart(headerText, headerColoredText, color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, headerText, headerColoredText, color]);

  return (
    <LazyMotion features={domAnimation} key={id}>
      <S.ItemWrapper
        role="listitem"
        data-title={heading}
        backgroundColor={color}
        textColor={gridViewTextColor}
        defaultBlackHeading={defaultBlackHeading}
        onHoverStart={() =>
          !isMobile && onHoverStart(headerText, headerColoredText, color)
        }
        onHoverEnd={() => !isMobile && onHoverEnd()}
        isMobile={isMobile}
        {...tilesMotionProps}
        {...rest}
      >
        <LinkComp href={mobileRedirectionCond}>
          <ContentMedia
            media={mediaType === 'image' ? image : video}
            mediaType={mediaType}
            aspectRatio={aspectRatio || '_16x9'}
            videoErrorMessage={videoErrorMessage}
          />
          {isMobile && (
            <S.ImageOverlay
              initial={{ opacity: 0 }}
              animate={isDoubleClicked ? { opacity: 1 } : { opacity: 0 }}
              onClick={handleOnMobileClick}
              backgroundColor={color}
            >
              <Text size="filter" text={heading} />
            </S.ImageOverlay>
          )}
        </LinkComp>
      </S.ItemWrapper>
    </LazyMotion>
  );
};

GridItem.defaultProps = defaultProps;

export default memo(GridItem);
